import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { feeSchema, type FeeFormInput, type FeeFormOutput } from '../schemas/fee.schema';
import { Form, Input, Button, Label, Select } from '@/shared/components';
import { useFeeReferenceDataQuery } from '../hooks/useFeeReferenceDataQuery';
import type { FeeRecord } from '../services/smr-admin';

interface FeeFormProps {
    initialValues?: Partial<FeeRecord>;
    onSubmit: SubmitHandler<FeeFormOutput>;
    isLoading?: boolean;
    submitLabel?: string;
}

export function FeeForm({ initialValues, onSubmit, isLoading = false, submitLabel = 'Save' }: FeeFormProps) {
    const { data: refData, isLoading: isRefLoading } = useFeeReferenceDataQuery();

    const transactionTypeOptions = (refData?.transaction_types ?? [])
        .filter((t) => t.is_fee_eligible)
        .map((t) => ({ value: t.id, label: `${t.name} (${t.code})` }));

    const { handleSubmit, control, formState: { isSubmitting } } = useForm<FeeFormInput, unknown, FeeFormOutput>({
        resolver: zodResolver(feeSchema),
        defaultValues: {
            name: initialValues?.name ?? '',
            transaction_type_id: initialValues?.transaction_type_id ?? '',
        },
    });

    return (
        <Form className="space-y-4 max-w-2xl" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                    <Label htmlFor="name">Name</Label>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Input id="name" placeholder="Fee name" value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
                        )}
                    />
                </div>
                <div>
                    <Label htmlFor="transaction_type_id">Transaction Type</Label>
                    <Controller
                        name="transaction_type_id"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Select
                                id="transaction_type_id"
                                placeholder={isRefLoading ? 'Loading…' : 'Select transaction type'}
                                options={transactionTypeOptions}
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                isLoading={isRefLoading}
                                error={!!error}
                                hint={error?.message}
                            />
                        )}
                    />
                </div>
            </div>
            <Button size="sm" disabled={isLoading || isSubmitting}>
                {isLoading || isSubmitting ? 'Saving...' : submitLabel}
            </Button>
        </Form>
    );
}
